import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { computed, signal } from '@preact/signals-react';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';

export type SymbolListKind = 'include' | 'exclude';

export interface SymbolListConditionInit {
    id: string;
    kind: SymbolListKind;
    symbols: ReadonlyArray<string>;
}

interface SymbolListConditionState {
    readonly kind: SymbolListKind;
    readonly symbols: ReadonlyArray<string>;
}

type SymbolListConditionAction =
    | { type: 'ADD_SYMBOL'; symbol: string }
    | { type: 'REMOVE_SYMBOL'; symbol: string }
    | { type: 'SET_SYMBOLS'; symbols: ReadonlyArray<string> }
    | { type: 'CLEAR' };

function reducer(state: SymbolListConditionState, action: SymbolListConditionAction): SymbolListConditionState {
    switch (action.type) {
        case 'ADD_SYMBOL':
            if (state.symbols.includes(action.symbol)) return state;
            return { ...state, symbols: [...state.symbols, action.symbol] };
        case 'REMOVE_SYMBOL':
            if (!state.symbols.includes(action.symbol)) return state;
            return { ...state, symbols: state.symbols.filter((s) => s !== action.symbol) };
        case 'SET_SYMBOLS':
            if (state.symbols === action.symbols) return state;
            return { ...state, symbols: action.symbols };
        case 'CLEAR':
            if (state.symbols.length === 0) return state;
            return { ...state, symbols: [] };
        default:
            throw new UnreachableCaseError(action);
    }
}

/**
 * Explicit list of ticker symbols that always pass (`kind: 'include'`) or
 * never pass (`kind: 'exclude'`) the universe filter, independent of any
 * indicator-driven criteria. `kind` is fixed at construction.
 */
export class SymbolListConditionModel {
    readonly type = 'symbol-list' as const;

    readonly #state: Signal<SymbolListConditionState>;

    readonly id: string;
    readonly kind: ReadonlySignal<SymbolListKind>;
    readonly symbols: ReadonlySignal<ReadonlyArray<string>>;
    readonly isEmpty: ReadonlySignal<boolean>;
    readonly validationErrors: ReadonlySignal<string[]>;
    readonly isValid: ReadonlySignal<boolean>;

    constructor(init: SymbolListConditionInit) {
        this.id = init.id;
        this.#state = signal<SymbolListConditionState>({
            kind: init.kind,
            symbols: init.symbols,
        });

        this.kind = computed(() => this.#state.value.kind);
        this.symbols = computed(() => this.#state.value.symbols);
        this.isEmpty = computed(() => this.#state.value.symbols.length === 0);
        this.validationErrors = computed((): string[] => []);
        this.isValid = computed(() => true);
    }

    #dispatch(action: SymbolListConditionAction): void {
        this.#state.value = reducer(this.#state.value, action);
    }

    addSymbol(symbol: string): void {
        this.#dispatch({ type: 'ADD_SYMBOL', symbol });
    }

    removeSymbol(symbol: string): void {
        this.#dispatch({ type: 'REMOVE_SYMBOL', symbol });
    }

    setSymbols(symbols: ReadonlyArray<string>): void {
        this.#dispatch({ type: 'SET_SYMBOLS', symbols });
    }

    clear(): void {
        this.#dispatch({ type: 'CLEAR' });
    }
}

export type SymbolListInstance = SymbolListConditionModel;
